import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import {PensionPlanAccount} from "@/integrations/supabase/pension.models.ts";

interface PensionPlanCardProps {
  pensionPlan: PensionPlanAccount & { convertedBalance?: number };
  currency: string;
  onEdit: () => void;
  onDelete: () => void;
  onClick?: () => void;
}

/**
 * PensionPlanCard component displays a single pension plan with details
 * and action buttons for editing and deleting.
 * 
 * Displays pension type badge and conditionally shows employer details
 * for occupational pension plans.
 * 
 * @param pensionPlan - The pension plan to display
 * @param currency - The selected currency for display
 * @param onEdit - Callback function when edit button is clicked
 * @param onDelete - Callback function when delete button is clicked
 * @param onClick - Optional callback function when card is clicked (alternative to default navigation)
 * 
 * @example
 * <PensionPlanCard
 *   pensionPlan={pensionPlan}
 *   currency="USD"
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 * />
 */
export function PensionPlanCard({ pensionPlan, currency, onEdit, onDelete, onClick }: PensionPlanCardProps) {
  const navigate = useNavigate();
  // FIXME: Handle case where pension_metadata is undefined. This is a rudimentary fix, this should be handled in the
  //  data layer, not here.
  const metadata = pensionPlan.pension_metadata;
  const hasMetadata = metadata.length > 0;
  const pensionType = hasMetadata ? metadata?.[0].pension_type : "Unknown";
  const isOccupational = hasMetadata ? metadata?.[0].pension_type === 'occupational' : false;

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const formatInterestRate = (rate: number) => {
    return `${rate.toFixed(2)}%`;
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  // Use converted balance if available and currency is different, otherwise use original balance
  const displayBalance = pensionPlan.convertedBalance !== undefined && pensionPlan.currency !== currency
    ? pensionPlan.convertedBalance
    : pensionPlan.balance;
  
  const displayCurrency = pensionPlan.convertedBalance !== undefined && pensionPlan.currency !== currency
    ? currency
    : pensionPlan.currency;

  // Get pension type badge text
  const getPensionTypeBadge = () => {
    switch (pensionType) {
      case 'government':
        return 'Government';
      case 'occupational':
        return 'Occupational';
      case 'individual':
        return 'Individual';
      default:
        return 'Unknown';
    }
  };

  // Get badge variant based on pension type
  const getBadgeVariant = () => {
    switch (pensionType) {
      case 'government':
        return 'default';
      case 'occupational':
        return 'secondary';
      case 'individual':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent click event from bubbling to parent elements
    e.stopPropagation();
    
    // If custom onClick handler is provided, use it
    if (onClick) {
      onClick();
    } else {
      // Default behavior: navigate to investment detail view
      navigate(`/investments/${pensionPlan.id}`);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };


  return (
    <Card 
      className="w-full cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={handleCardClick}
      role="article"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      aria-label={`${getPensionTypeBadge()} pension plan: ${pensionPlan.fund_name}. Balance: ${formatCurrency(displayBalance, displayCurrency)}`}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-lg font-semibold">
              {pensionPlan.fund_name}
            </CardTitle>
            <Badge variant={getBadgeVariant()}>
              {getPensionTypeBadge()}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{pensionPlan.fund_manager}</p>
        </div>
        {pensionPlan.currency !== currency && (
          <Badge variant="outline" className="ml-2 shrink-0">
            Original: {pensionPlan.currency}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Account Number:</span>
            <span className="font-medium">{pensionPlan.account_number}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Current Balance:</span>
            <span className="font-medium">
              {formatCurrency(displayBalance, displayCurrency)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Interest Rate:</span>
            <span className="font-medium">
              {formatInterestRate(pensionPlan.interest_rate)}
            </span>
          </div>
          
          {/* Conditionally display employer details for occupational pension plans */}
          {isOccupational && pensionPlan.pension_metadata && (
            <>
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-muted-foreground">Employer:</span>
                <span className="font-medium">{pensionPlan.pension_metadata?.[0].employer_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Employer Contribution:</span>
                <span className="font-medium">
                  {pensionPlan.pension_metadata?.[0].employer_contribution_percentage !== null
                    ? formatPercentage(pensionPlan.pension_metadata?.[0].employer_contribution_percentage)
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Employee Contribution:</span>
                <span className="font-medium">
                  {pensionPlan.pension_metadata?.[0].employee_contribution_percentage !== null
                    ? formatPercentage(pensionPlan.pension_metadata?.[0].employee_contribution_percentage)
                    : 'N/A'}
                </span>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditClick}
            className="flex-1"
            aria-label={`Edit ${pensionPlan.fund_name}`}
          >
            <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeleteClick}
            className="flex-1 text-red-600 hover:text-red-700"
            aria-label={`Delete ${pensionPlan.fund_name}`}
          >
            <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
